import apiClient from '../../../api/client';
import type {
  ClienteEvento,
  ClienteResponse,
  ClienteUbicacionPreviewResponse,
  ClienteUbicacionUpdateResponse,
  ClienteResumenResponse,
  CreateClienteDto,
  UpdateClienteDto,
  QueryClientesDto,
  ClienteServiciosResponse,
  Paginated,
  CobroClienteResponse,
  VisitaCobroResponse,
  EstadoCuentaResponse,
  QueryEstadoCuentaDto,
  EstadoCuentaExportFormat,
  EstadoCuentaExportFilters,
  EstadoCuentaSerieMensualResponse,
  QueryEstadoCuentaSerieMensualDto,
  EstadoCuentaSimpleResponse,
  QueryEstadoCuentaSimpleDto,
  EstadoCuentaSimpleExportFilters,
  ClientDeactivationPreviewResponse,
  ClientDeactivationResponse,
  PreviewClientDeactivationDto,
  ConfirmClientDeactivationDto,
  CambioDiaFacturacionPreviewDto,
  CambioDiaFacturacionPreviewResponse,
  CambioDiaFacturacionDto,
  CambioDiaFacturacionResponse,
  ClientePrepagoCobertura,
  ClienteDetalleResponse,
} from '@aid/shared';

export type {
  ClienteEvento,
  ClienteResponse,
  ClienteUbicacionPreviewResponse,
  ClienteUbicacionUpdateResponse,
  ClienteResumenResponse,
  CreateClienteDto,
  UpdateClienteDto,
  QueryClientesDto,
  EstadoCuentaResponse,
  QueryEstadoCuentaDto,
  EstadoCuentaExportFormat,
  EstadoCuentaExportFilters,
  EstadoCuentaSerieMensualResponse,
  QueryEstadoCuentaSerieMensualDto,
  EstadoCuentaSimpleResponse,
  QueryEstadoCuentaSimpleDto,
  EstadoCuentaSimpleExportFilters,
  ClientDeactivationPreviewResponse,
  ClientDeactivationResponse,
  PreviewClientDeactivationDto,
  ConfirmClientDeactivationDto,
  CambioDiaFacturacionPreviewDto,
  CambioDiaFacturacionPreviewResponse,
  CambioDiaFacturacionDto,
  CambioDiaFacturacionResponse,
  ClientePrepagoCobertura,
  ClienteDetalleResponse,
};

export async function getClientes(params: QueryClientesDto): Promise<Paginated<ClienteResponse>> {
  const { data } = await apiClient.get<Paginated<ClienteResponse>>('/admin/clientes', { params });
  return data;
}

export async function getCliente(id: number): Promise<ClienteDetalleResponse> {
  const { data } = await apiClient.get<ClienteDetalleResponse>(`/admin/clientes/${id}`);
  return data;
}

export async function createCliente(dto: CreateClienteDto): Promise<ClienteResponse> {
  const { data } = await apiClient.post<ClienteResponse>('/admin/clientes', dto);
  return data;
}

export async function updateCliente(id: number, dto: UpdateClienteDto): Promise<ClienteResponse> {
  const { data } = await apiClient.patch<ClienteResponse>(`/admin/clientes/${id}`, dto);
  return data;
}

export async function previewClienteUbicacion(
  id: number,
  dto: { latitud: number; longitud: number },
): Promise<ClienteUbicacionPreviewResponse> {
  const { data } = await apiClient.post<ClienteUbicacionPreviewResponse>(
    `/admin/clientes/${id}/ubicacion/preview`,
    dto,
  );
  return data;
}

export async function syncClienteUbicacionFromPago(
  idPago: number,
): Promise<ClienteUbicacionUpdateResponse> {
  const { data } = await apiClient.post<ClienteUbicacionUpdateResponse>(
    `/admin/pagos/${idPago}/ubicacion-cliente`,
  );
  return data;
}

export async function deleteCliente(id: number): Promise<void> {
  await apiClient.delete(`/admin/clientes/${id}`);
}

export async function previewClientDeactivation(
  id: number,
  dto: PreviewClientDeactivationDto,
): Promise<ClientDeactivationPreviewResponse> {
  const { data } = await apiClient.post<ClientDeactivationPreviewResponse>(
    `/admin/clientes/${id}/deactivation/preview`,
    dto,
  );
  return data;
}

export async function confirmClientDeactivation(
  id: number,
  dto: ConfirmClientDeactivationDto,
): Promise<ClientDeactivationResponse> {
  const { data } = await apiClient.post<ClientDeactivationResponse>(
    `/admin/clientes/${id}/deactivation`,
    dto,
  );
  return data;
}

export async function getClientDeactivation(
  id: number,
): Promise<ClientDeactivationResponse | null> {
  const { data } = await apiClient.get<ClientDeactivationResponse | null>(
    `/admin/clientes/${id}/deactivation`,
  );
  return data;
}

export async function previewCambioDiaFacturacion(
  id: number,
  dto: CambioDiaFacturacionPreviewDto,
): Promise<CambioDiaFacturacionPreviewResponse> {
  const { data } = await apiClient.post<CambioDiaFacturacionPreviewResponse>(
    `/admin/clientes/${id}/cambio-dia-facturacion/preview`,
    dto,
  );
  return data;
}

export async function cambioDiaFacturacion(
  id: number,
  dto: CambioDiaFacturacionDto,
): Promise<CambioDiaFacturacionResponse> {
  const { data } = await apiClient.post<CambioDiaFacturacionResponse>(
    `/admin/clientes/${id}/cambio-dia-facturacion`,
    dto,
  );
  return data;
}

export async function getClienteServicios(
  id: number,
  params: { limit?: number; offset?: number; estado?: number; incluirBajas?: boolean },
): Promise<ClienteServiciosResponse> {
  const { data } = await apiClient.get<ClienteServiciosResponse>(
    `/admin/clientes/${id}/servicios`,
    { params },
  );
  return data;
}

export async function getClienteResumen(id: number): Promise<ClienteResumenResponse> {
  const { data } = await apiClient.get<ClienteResumenResponse>(`/admin/clientes/${id}/resumen`);
  return data;
}

/**
 * Lifecycle events (public.cliente_evento) for the cliente 360 view:
 * ALTA / BAJA / REACTIVACION rows, newest first, no pagination.
 * Read-only — events are emitted exclusively by the billing domain.
 */
export async function getClienteEventos(id: number): Promise<ClienteEvento[]> {
  const { data } = await apiClient.get<ClienteEvento[]>(`/admin/clientes/${id}/eventos`);
  return data;
}

/**
 * Bills for the cliente 360 view. Unlike the global /admin/cobros endpoint,
 * this one resolves `estado: { key, value }` (VIGENTE vs PAGADA) so the
 * detail view can compute mora and render correct status tags.
 */
export async function getClienteCobros(
  id: number,
  params: { limit?: number; offset?: number; fechaDesde?: string; fechaHasta?: string },
): Promise<Paginated<CobroClienteResponse>> {
  const { data } = await apiClient.get<Paginated<CobroClienteResponse>>(
    `/admin/clientes/${id}/cobros`,
    {
      params,
    },
  );
  return data;
}

/**
 * Visitas de cobro del cliente (GET /admin/clientes/:id/visitas, Fase D).
 * Historico no financiero: nunca toca pagos, saldos ni recibos.
 */
export async function getClienteVisitas(
  id: number,
  params: { limit?: number; offset?: number },
): Promise<Paginated<VisitaCobroResponse>> {
  const { data } = await apiClient.get<Paginated<VisitaCobroResponse>>(
    `/admin/clientes/${id}/visitas`,
    {
      params,
    },
  );
  return data;
}

export async function getClienteEstadoCuenta(
  id: number,
  params: QueryEstadoCuentaDto,
): Promise<EstadoCuentaResponse> {
  const { data } = await apiClient.get<EstadoCuentaResponse>(
    `/admin/clientes/${id}/estado-cuenta`,
    { params },
  );
  return data;
}

export async function getClienteEstadoCuentaSimple(
  id: number,
  params: QueryEstadoCuentaSimpleDto,
): Promise<EstadoCuentaSimpleResponse> {
  const { data } = await apiClient.get<EstadoCuentaSimpleResponse>(
    `/admin/clientes/${id}/estado-cuenta/simple`,
    { params },
  );
  return data;
}

export async function exportClienteEstadoCuenta(
  id: number,
  format: EstadoCuentaExportFormat,
  filters: EstadoCuentaExportFilters,
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/admin/clientes/${id}/estado-cuenta/export`, {
    params: { ...filters, format },
    responseType: 'blob',
  });
  return data;
}

export async function exportClienteEstadoCuentaSimple(
  id: number,
  format: EstadoCuentaExportFormat,
  filters: EstadoCuentaSimpleExportFilters,
): Promise<Blob> {
  const { data } = await apiClient.get<Blob>(`/admin/clientes/${id}/estado-cuenta/simple/export`, {
    params: { ...filters, format },
    responseType: 'blob',
  });
  return data;
}

export async function getClienteSerieMensual(
  id: number,
  params: QueryEstadoCuentaSerieMensualDto,
): Promise<EstadoCuentaSerieMensualResponse> {
  const { data } = await apiClient.get<EstadoCuentaSerieMensualResponse>(
    `/admin/clientes/${id}/estado-cuenta/serie-mensual`,
    { params },
  );
  return data;
}
